/**
 * Email validation utility with typo detection
 * Uses validator.js for robust email format validation
 * Only allows signup with whitelisted email domains
 */

import validator from 'validator';

// Gmail domains (for typo detection)
const GMAIL_DOMAINS = ['gmail.com', 'googlemail.com'];

// Allowed email domains for signup
const ALLOWED_DOMAINS = [
  // Google
  'gmail.com',
  'googlemail.com',
  
  // Yahoo
  'yahoo.com',
  'yahoo.co.uk',
  
  // Microsoft
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'microsoft.com',
  
  // Apple
  'icloud.com',
  'me.com',
  'mac.com',
  'apple.com',
  
  // Other major providers
  'aol.com',
  'proton.me',
  'protonmail.com',
  'zoho.com',
  'yandex.com',
  'gmx.com',
  'mail.com',
  'fastmail.com',
  
  // India
  'rediffmail.com',
  
  // China
  'qq.com',
  '163.com',
  '126.com',
  'sina.com',
  
  // South Korea
  'naver.com',
  'daum.net',
  
  // Russia
  'rambler.ru',
  
  // Germany
  'web.de',
  't-online.de',
  
  // France
  'orange.fr',
  'laposte.net',
  
  // Tech companies
  'amazon.com',
  'meta.com',
  'github.com',
  'gitlab.com',
  
  // Custom domains
  'azooca.com',
  
  // Temporary/Disposable email services (included per user request)
  'mailinator.com',
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'yopmail.com',
  'throwawaymail.com',
  'fakeinbox.com',
  'maildrop.cc',
];

// Common typos and their corrections for Gmail domains (helpful suggestions)
const TYPO_MAP: { [key: string]: string } = {
  'gmial.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.coom': 'gmail.com',
  'gmail.om': 'gmail.com',
  'gmail.c': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmailcom': 'gmail.com',
  'gmaiil.com': 'gmail.com',
  'gmailll.com': 'gmail.com',
  'gmail.co.uk': 'gmail.com',
  'gmail.uk': 'gmail.com',
  'googlemail.co': 'googlemail.com',
  'googlemail.cm': 'googlemail.com',
  'googlemail.con': 'googlemail.com',
  'googlemai.com': 'googlemail.com',
  'googlemaiil.com': 'googlemail.com',
};

export interface GmailValidationResult {
  isValid: boolean;
  errorMessage?: string;
  suggestion?: string;
  correctedEmail?: string;
  suggestGoogleSignup?: boolean; // Deprecated - kept for backward compatibility
}

/**
 * Validates if an email is a valid email address from an allowed domain
 * Uses validator.js for email format validation
 * Only allows signup with whitelisted email domains
 * @param email - The email address to validate
 * @returns Validation result with error message and suggestions
 */
export const validateGmail = (email: string): GmailValidationResult => {
  // Trim whitespace
  const trimmedEmail = email.trim();

  // Check if empty
  if (!trimmedEmail) {
    return {
      isValid: false,
      errorMessage: 'Email is required',
    };
  }

  // Use validator.js for robust email format validation
  if (!validator.isEmail(trimmedEmail, { allow_utf8_local_part: false })) {
    return {
      isValid: false,
      errorMessage: 'Enter a valid email address',
    };
  }

  // Extract domain (convert to lowercase for comparison)
  const parts = trimmedEmail.split('@');
  if (parts.length !== 2) {
    return {
      isValid: false,
      errorMessage: 'Enter a valid email address',
    };
  }

  const domain = parts[1].toLowerCase();
  const localPart = parts[0]; // Keep local part as-is

  // Check for typos (helpful suggestions for Gmail domains)
  if (TYPO_MAP[domain]) {
    const correctedDomain = TYPO_MAP[domain];
    const correctedEmail = `${localPart}@${correctedDomain}`;
    return {
      isValid: false,
      errorMessage: `Did you mean ${correctedEmail}?`,
      suggestion: correctedEmail,
      correctedEmail,
    };
  }

  // Check if domain is in the allowed list
  if (ALLOWED_DOMAINS.includes(domain)) {
    return {
      isValid: true,
    };
  }

  // Domain is not in the allowed list
  return {
    isValid: false,
    errorMessage: 'Please use an email address from a supported provider. You can also sign up with Google.',
    suggestGoogleSignup: true,
  };
};

/**
 * Checks if an email domain is a Gmail domain
 * @param domain - The domain to check
 * @returns true if domain is gmail.com or googlemail.com
 */
export const isGmailDomain = (domain: string): boolean => {
  return GMAIL_DOMAINS.includes(domain.toLowerCase());
};

/**
 * Gets a suggested correction for a typo domain
 * @param domain - The domain that might be a typo
 * @returns The corrected domain or null
 */
export const getTypoCorrection = (domain: string): string | null => {
  return TYPO_MAP[domain.toLowerCase()] || null;
};

